import { useEffect, useRef, useState } from 'react'

export type DiscriminatorItem<
  Object extends object,
  DiscriminatorKey extends keyof Object,
> = {
  key: DiscriminatorKey
  value: Object[DiscriminatorKey]
}

/**
 * Preserves selected object fields when a discriminator condition toggles off and restores
 * them when the same discriminator condition becomes active again.
 *
 * @param object Current object state that is evaluated and updated.
 * @param discriminator Key/value pairs that define when the preserved fields should be active.
 * @param preservedKeys Object keys to keep across discriminator toggles.
 * @param updateObject Callback used to apply object updates after save/restore operations.
 */
export const usePreserveFieldsAcrossDiscriminator = <
  Object extends object,
  PreservedKeys extends keyof Object,
  DiscriminatorValues extends DiscriminatorItem<Object, keyof Object>[],
>({
  object,
  discriminator,
  preservedKeys,
  updateObject,
}: {
  object: Object
  discriminator: DiscriminatorValues
  preservedKeys: PreservedKeys[]
  updateObject: (object: Object) => void
}) => {
  // Keep the initial inputs stable so effect logic is driven by object changes only.
  const stableDiscriminatorRef = useRef(discriminator)
  const stablePreservedKeysRef = useRef(preservedKeys)
  const stableUpdateObjectRef = useRef(updateObject)
  const previousIsCurrentDiscriminatorSelectedRef = useRef(
    discriminator.every(({ key, value }) => object[key] === value),
  )
  const pendingRestoreRef = useRef<Partial<Pick<Object, PreservedKeys>> | null>(
    null,
  )
  const [savedObject, setSavedObject] = useState<
    Partial<Pick<Object, PreservedKeys>>
  >({})

  // Do not preserve discriminator keys themselves; they define section visibility/state.
  const nonDiscriminatorPreservedKeys = stablePreservedKeysRef.current.filter(
    key =>
      !stableDiscriminatorRef.current.some(
        discriminatorItem => discriminatorItem.key === key,
      ),
  )
  // Current selection state for the configured discriminator pair(s).
  const isCurrentDiscriminatorSelected = stableDiscriminatorRef.current.every(
    ({ key, value }) => object[key] === value,
  )

  useEffect(() => {
    // Detect transition direction between previous and current discriminator states.
    const wasCurrentDiscriminatorSelected =
      previousIsCurrentDiscriminatorSelectedRef.current
    const hasUsedDiscriminatorBeenUnselected =
      wasCurrentDiscriminatorSelected && !isCurrentDiscriminatorSelected

    if (hasUsedDiscriminatorBeenUnselected) {
      // Clear any pending restore and save current values before removing preserved keys.
      pendingRestoreRef.current = null
      const nextSavedObject = nonDiscriminatorPreservedKeys.reduce(
        (acc, key) => {
          acc[key] = object[key]
          return acc
        },
        {} as Partial<Pick<Object, PreservedKeys>>,
      )
      setSavedObject(nextSavedObject)

      const nextObject = { ...object }
      nonDiscriminatorPreservedKeys.forEach(key => {
        // Remove hidden field values from object when discriminator turns off.
        delete nextObject[key]
      })
      stableUpdateObjectRef.current(nextObject)
    }

    const hasUsedDiscriminatorBeenSelected =
      !wasCurrentDiscriminatorSelected && isCurrentDiscriminatorSelected

    if (hasUsedDiscriminatorBeenSelected) {
      // Build a patch from saved values and restore immediately when object differs.
      let needUpdate = false
      const nextObject = { ...object }
      const restorePatch = {} as Partial<Pick<Object, PreservedKeys>>
      nonDiscriminatorPreservedKeys.forEach(key => {
        if (key in savedObject) {
          const savedValue = savedObject[key]
          restorePatch[key] = savedValue
          if (savedValue !== object[key]) {
            nextObject[key] = savedValue as Object[PreservedKeys]
            needUpdate = true
          }
        }
      })
      pendingRestoreRef.current = restorePatch

      if (needUpdate) {
        stableUpdateObjectRef.current(nextObject)
      }
    }

    if (isCurrentDiscriminatorSelected && pendingRestoreRef.current) {
      // Keep retrying restore while selected; clear pending patch once fully applied.
      let needRestore = false
      const nextObject = { ...object }
      nonDiscriminatorPreservedKeys.forEach(key => {
        if (pendingRestoreRef.current && key in pendingRestoreRef.current) {
          const pendingValue = pendingRestoreRef.current?.[key]
          if (pendingValue !== object[key]) {
            nextObject[key] = pendingValue as Object[PreservedKeys]
            needRestore = true
          }
        }
      })

      if (needRestore) {
        stableUpdateObjectRef.current(nextObject)
      } else {
        // All pending values are applied, so cleanup pending restore state.
        pendingRestoreRef.current = null
      }
    }
    // Persist current state for transition detection in the next effect run.
    previousIsCurrentDiscriminatorSelectedRef.current =
      isCurrentDiscriminatorSelected
  }, [
    isCurrentDiscriminatorSelected,
    nonDiscriminatorPreservedKeys,
    object,
    savedObject,
  ])

  return savedObject
}
